import React, { useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { WeekSelectorProps } from '../../types';

/**
 * WeekSelector - Discrete week slider with keyboard navigation
 * Features:
 * - Discrete ticks for weeks 1-18
 * - Keyboard arrow key navigation
 * - Visual indicators for completed vs upcoming weeks
 * - Click to select any week
 */
const WeekSelector: React.FC<WeekSelectorProps> = ({
  selectedWeek,
  maxWeek,
  onWeekChange,
  completedWeeks
}) => {
  const weeks = Array.from({ length: maxWeek }, (_, i) => i + 1);

  // Keyboard navigation
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft' && selectedWeek > 1) {
      onWeekChange(selectedWeek - 1);
    } else if (event.key === 'ArrowRight' && selectedWeek < maxWeek) {
      onWeekChange(selectedWeek + 1);
    }
  }, [selectedWeek, maxWeek, onWeekChange]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const goToPreviousWeek = () => {
    if (selectedWeek > 1) {
      onWeekChange(selectedWeek - 1);
    }
  };

  const goToNextWeek = () => {
    if (selectedWeek < maxWeek) {
      onWeekChange(selectedWeek + 1);
    }
  };

  const isWeekCompleted = (week: number) => completedWeeks.includes(week);

  return (
    <div className="flex items-center space-x-4">
      {/* Previous Week Button */}
      <button
        onClick={goToPreviousWeek}
        disabled={selectedWeek <= 1}
        className="p-2 rounded-lg bg-background-secondary text-text-secondary hover:bg-background-hover hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-fieldline"
        aria-label="Previous week"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {/* Week Selector Track */}
      <div className="flex items-center space-x-1">
        <span className="text-text-secondary text-sm font-medium min-w-[24px]">W1</span>
        
        {/* Week Dots/Ticks */}
        <div className="flex items-center space-x-1 px-4">
          {weeks.map((week) => {
            const isSelected = week === selectedWeek;
            const isCompleted = isWeekCompleted(week);
            const isAvailable = week <= Math.max(...completedWeeks) + 1; // Allow one week ahead
            
            return (
              <button
                key={week}
                onClick={() => onWeekChange(week)}
                disabled={!isAvailable}
                className={`relative w-4 h-4 rounded-full transition-all duration-200 hover:scale-110 ${
                  isSelected
                    ? 'bg-accent-primary ring-2 ring-accent-primary/50 transform scale-125'
                    : isCompleted
                    ? 'bg-accent-primary/50 hover:bg-accent-primary/70'
                    : isAvailable
                    ? 'bg-background-hover hover:bg-text-tertiary'
                    : 'bg-background-secondary cursor-not-allowed opacity-50'
                }`}
                aria-label={`Week ${week}${isCompleted ? ' (completed)' : ''}`}
                title={`Week ${week}${isCompleted ? ' (completed)' : isAvailable ? '' : ' (upcoming)'}`}
              >
                {/* Active week indicator */}
                {isSelected && (
                  <div className="absolute inset-0 rounded-full bg-accent-primary animate-pulse opacity-50" />
                )}

                {/* Week number for selected week */}
                {isSelected && (
                  <span className="absolute inset-0 flex items-center justify-center text-background-primary text-xs font-bold">
                    {week}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        
        <span className="text-text-secondary text-sm font-medium min-w-[32px]">W{maxWeek}</span>
      </div>

      {/* Next Week Button */}
      <button
        onClick={goToNextWeek}
        disabled={selectedWeek >= maxWeek}
        className="p-2 rounded-lg bg-background-secondary text-text-secondary hover:bg-background-hover hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-fieldline"
        aria-label="Next week"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {/* Current Selection Display */}
      <div className="flex items-center space-x-2 ml-4">
        <div className="text-text-primary font-bold text-lg">
          Week {selectedWeek}
        </div>
        {isWeekCompleted(selectedWeek) && (
          <div className="px-2 py-1 bg-accent-primary/15 text-accent-primary text-xs rounded-md font-mono font-medium">
            COMPLETED
          </div>
        )}
      </div>
    </div>
  );
};

export default WeekSelector;
